import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiClose, mdiFileImportOutline, mdiAccountGroupOutline, mdiCompassOutline } from '@mdi/js';
import { api } from '../services/api';
import { signOutUser } from '../services/firebase';
import { dayToDayAccounts } from '../utils/accounts';
import { getStoredTheme, setStoredTheme, THEME_OPTIONS } from '../services/theme';
import Select from './Select';
import '../styles/AccountPanel.css';

const DEFAULT_ACCOUNT_KEY = 'default_account_id';
const THEME_KEY = 'theme';

const AccountPanel = ({ user, onClose, onReplayTour }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [defaultAccountId, setDefaultAccountId] = useState(null);
  const [theme, setTheme] = useState(() => getStoredTheme());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [accountsRes, settingsRes] = await Promise.all([
          api.get('/accounts'),
          api.get('/usersettings').catch(() => ({ data: {} })),
        ]);
        const openAccounts = dayToDayAccounts((accountsRes.data || []).filter((a) => a.is_open));
        setAccounts(openAccounts);
        const stored = settingsRes.data?.[DEFAULT_ACCOUNT_KEY];
        if (stored) setDefaultAccountId(parseInt(stored, 10));
        const storedTheme = settingsRes.data?.[THEME_KEY];
        if (storedTheme && storedTheme !== theme) {
          setTheme(storedTheme);
          setStoredTheme(storedTheme);
        }
      } catch (err) {
        setError('Failed to load settings.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const accountOptions = accounts.map((a) => ({ value: a.id, label: a.name }));

  const handleThemeChange = (option) => {
    setTheme(option.value);
    setStoredTheme(option.value);
    // Persist to the backend in the background; don't block the UI.
    api.put(`/usersettings/${THEME_KEY}`, { value: option.value }).catch(() => {
      // Non-fatal — the local preference is already applied.
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const value = defaultAccountId != null ? String(defaultAccountId) : '';
      await api.put(`/usersettings/${DEFAULT_ACCOUNT_KEY}`, { value });
      // Mirror the existing convention of caching settings in localStorage.
      try {
        const cached = JSON.parse(localStorage.getItem('userSettings') || '{}');
        cached[DEFAULT_ACCOUNT_KEY] = value;
        localStorage.setItem('userSettings', JSON.stringify(cached));
      } catch {
        // localStorage may be unavailable; non-fatal.
      }
      setSaved(true);
    } catch (err) {
      setError('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const displayName = user?.displayName || user?.email?.split('@')[0] || '';
  const email = user?.email || '';

  return (
    <>
      <div className="account-panel-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="edit-panel account-panel" role="dialog" aria-label="Account settings">
        <div className="panel-header">
          <h3>Account</h3>
          <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close account panel">
            <Icon path={mdiClose} size={1} />
          </button>
        </div>

        <div className="account-panel__user">
          <div className="account-panel__avatar" aria-hidden="true">
            {displayName ? displayName.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="account-panel__user-info">
            <div className="account-panel__name">{displayName}</div>
            <div className="account-panel__email">{email}</div>
          </div>
        </div>

        <hr className="separator" />

        {loading ? (
          <p className="account-panel__hint">Loading settings…</p>
        ) : (
          <div className="account-panel__section">
            <label className="account-panel__label">Default account</label>
            <Select
              options={accountOptions}
              onSelect={(option) => setDefaultAccountId(option.value)}
              value={defaultAccountId}
              placeholder="Select an account"
            />
            <p className="account-panel__hint">
              The account pre-selected when creating a new transaction.
            </p>
          </div>
        )}

        <hr className="separator" />

        <div className="account-panel__section">
          <label className="account-panel__label">Theme</label>
          <Select
            options={THEME_OPTIONS}
            onSelect={handleThemeChange}
            value={theme}
            placeholder="Select a theme"
          />
          <p className="account-panel__hint">
            Choose Light, Dark, or follow the system setting.
          </p>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="form-buttons">
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {saved && <p className="account-panel__saved">Saved.</p>}

        <hr className="separator" />

        <button
          type="button"
          className="btn btn--secondary account-panel__link"
          onClick={() => {
            onClose();
            navigate('/family');
          }}
          data-tour="family-management"
        >
          <Icon path={mdiAccountGroupOutline} size={1} />
          Family Management
        </button>

        <hr className="separator" />

        <button
          type="button"
          className="btn btn--secondary account-panel__link"
          onClick={() => {
            onClose();
            navigate('/manage-import-mappings');
          }}
        >
          <Icon path={mdiFileImportOutline} size={1} />
          Manage Import Mappings
        </button>

        <hr className="separator" />

        {onReplayTour && (
          <button
            type="button"
            className="btn btn--secondary account-panel__link"
            onClick={onReplayTour}
          >
            <Icon path={mdiCompassOutline} size={1} />
            Replay Tour
          </button>
        )}

        <hr className="separator" />

        <button type="button" className="btn btn--secondary account-panel__logout" onClick={signOutUser}>
          Log out
        </button>
      </aside>
    </>
  );
};

export default AccountPanel;